import { useEffect, useState, type FormEvent } from "react";
import { Head, Icon, Pill, useStudio } from "../components/ui";
import { FILES, PEOPLE, TEAM_IDS } from "../data/sample";
import { desktop, type DesktopEntry, type DesktopFolder } from "../lib/desktop";
import { cleanError, m365Available, openLink, useGraph, type DriveFile } from "../lib/m365";
import { TeamKnowledge } from "../components/TeamKnowledge";
import { FieldPhotos } from "../components/FieldPhotos";
import { L, T, Tf } from "../lib/i18n";

export function Files() {
  const { profile } = useStudio();
  // For a company (EduTask), Files is first of all its knowledge base: everything VERA should know about the company.
  const kb = Boolean(profile.external);
  const [tab, setTab] = useState<"team" | "local" | "knowledge" | "field">(kb ? "knowledge" : desktop ? "local" : "team");
  const knowledgeTab = <button role="tab" aria-selected={tab === "knowledge"} className={tab === "knowledge" ? "on" : ""} onClick={() => setTab("knowledge")}>{kb ? L("Knowledge base", "Baza e njohurive") : L("Team knowledge", "Njohuritë e ekipit")}</button>;
  // VERA Field (photos from the glasses) is for the field teams, not for EduTask.
  const field = profile.key !== "client-edutask";
  // "On this computer" needs VERA for desktop; companies get the desktop app later, so on the web they don't see the tab yet.
  const local = Boolean(desktop) || !profile.external;
  return (
    <>
      <Head title={L("Files", "Skedarët")} lead={kb
        ? L("Your knowledge base: everything VERA should know about your company, so she learns how you work. Your team's files from Google Drive, SharePoint and OneDrive are here too.", "Baza juaj e njohurive: gjithçka që VERA duhet të dijë për kompaninë tuaj, që të mësojë si punoni. Këtu janë edhe skedarët e ekipit nga Google Drive, SharePoint dhe OneDrive.")
        : L("Team files by workspace, and on VERA for desktop, the folders on your own computer that you choose to share with VERA.", "Skedarët e ekipit sipas hapësirës së punës, dhe në VERA për kompjuter, dosjet në kompjuterin tënd që zgjedh t'i ndash me VERA-n.")} />
      <div className="tabs" role="tablist" style={{ maxWidth: 720 }}>
        {kb && knowledgeTab}
        <button role="tab" aria-selected={tab === "team"} className={tab === "team" ? "on" : ""} onClick={() => setTab("team")}>{m365Available() ? T("SharePoint & OneDrive") : L("Team files", "Skedarët e ekipit")}</button>
        {local && <button role="tab" aria-selected={tab === "local"} className={tab === "local" ? "on" : ""} onClick={() => setTab("local")}>{L("On this computer", "Në këtë kompjuter")}</button>}
        {!kb && knowledgeTab}
        {field && <button role="tab" aria-selected={tab === "field"} className={tab === "field" ? "on" : ""} onClick={() => setTab("field")}>{L("From the field", "Nga terreni")}</button>}
      </div>
      {tab === "team" ? <TeamFiles /> : tab === "knowledge" ? <TeamKnowledge base={kb} /> : tab === "field" ? <FieldPhotos /> : <LocalFolders />}
    </>
  );
}

function TeamFiles() {
  if (m365Available()) return <LiveTeamFiles />;
  return <SampleTeamFiles />;
}

function LiveTeamFiles() {
  const { m365, consent, openConsent, toast } = useStudio();
  const on = m365.connected("files");
  const [connecting, setConnecting] = useState(false);
  // Connects right here, with the person's own Microsoft account: no detour through Connections.
  async function connect() {
    if (!consent.m365) { openConsent(); return; }
    setConnecting(true);
    try { await m365.connect("files"); toast(L("Connected. VERA can now read your SharePoint and OneDrive files for you.", "U lidh. VERA tani mund t'i lexojë skedarët e tu në SharePoint dhe OneDrive.")); }
    catch (e) { toast(cleanError(e)); } finally { setConnecting(false); }
  }
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const recent = useGraph<DriveFile[]>("files.recent", on && !search);
  const found = useGraph<DriveFile[]>("files.search", on && !!search, { q: search });
  if (!on) return <div className="card stack" style={{ alignItems: "flex-start", gap: 10 }}><h2>{L("Connect SharePoint & OneDrive", "Lidh SharePoint dhe OneDrive")}</h2><p className="mute">{L("See your recent files and search across everything you can open. VERA only reads; she never changes or shares your files.", "Shih skedarët e fundit dhe kërko në gjithçka që mund të hapësh. VERA vetëm lexon; ajo kurrë nuk i ndryshon e as nuk i ndan skedarët e tu.")}</p><button className="btn primary" disabled={connecting} onClick={() => void connect()}>{connecting ? L("Waiting for Microsoft…", "Po pret Microsoft-in…") : L("Connect SharePoint & OneDrive", "Lidh SharePoint dhe OneDrive")}</button></div>;
  const r = search ? found : recent;
  return (
    <>
      <form className="row" style={{ gap: 8 }} onSubmit={(e: FormEvent) => { e.preventDefault(); setSearch(q.trim()); }}>
        <label className="search" style={{ flex: 1, maxWidth: 520 }}><Icon name="search" size={16} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Search SharePoint and OneDrive", "Kërko në SharePoint dhe OneDrive")} /></label>
        <button className="btn primary" disabled={!q.trim()}>{L("Search", "Kërko")}</button>
        {search && <button type="button" className="btn" onClick={() => { setQ(""); setSearch(""); }}>{L("Show recent", "Shfaq të fundit")}</button>}
      </form>
      <div className="card tbl-wrap"><table><thead><tr><th>{search ? L(`Results for "${search}"`, `Rezultatet për "${search}"`) : L("Recent files", "Skedarët e fundit")}</th><th>{L("Where", "Ku")}</th><th>{L("Last changed by", "Ndryshuar së fundi nga")}</th><th>{L("Updated", "Përditësuar")}</th></tr></thead><tbody>
        {r.loading && !r.data ? <tr><td colSpan={4} className="faint">{L("Loading…", "Po ngarkohet…")}</td></tr>
          : r.error ? <tr><td colSpan={4} style={{ color: "var(--bad)" }}>{r.error}</td></tr>
          : !r.data?.length ? <tr><td colSpan={4} className="faint">{search ? L("Nothing found.", "Nuk u gjet asgjë.") : L("No recent files.", "Nuk ka skedarë të fundit.")}</td></tr>
          : r.data.map((f) => (
            <tr key={f.id ?? f.webUrl}><td><button className="filebtn" style={{ padding: 0 }} onClick={() => openLink(f.webUrl)}><Icon name="doc" size={16} /><b className="grow">{f.name}</b></button>{f.summary && <div className="faint" style={{ fontSize: 12 }}>{f.summary.slice(0, 140)}</div>}</td>
              <td><Pill tone="info">{f.where}</Pill></td><td className="faint">{f.by}</td><td className="faint">{f.modified ? new Date(f.modified).toLocaleDateString() : ""}</td></tr>))}
      </tbody></table></div>
      <span className="live">{L("Live from Microsoft 365 · read only", "Drejtpërdrejt nga Microsoft 365 · vetëm lexim")}</span>
    </>
  );
}

function SampleTeamFiles() {
  const { person, user } = useStudio();
  const me = person?.id ?? user.id;
  const mine = FILES.filter((f) => f.shared.includes(me));
  const spaces = [...new Set(mine.map((f) => f.workspace))];
  const [ws, setWs] = useState("all");
  const list = ws === "all" ? mine : mine.filter((f) => f.workspace === ws);
  const first = (id: string) => PEOPLE.find((p) => p.id === id)?.first ?? id;
  return (
    <>
      <div className="row" style={{ justifyContent: "space-between" }}>
        <div className="row">{[["all", L("Everything I can open", "Gjithçka që mund të hap")], ...spaces.map((s) => [s, s])].map(([id, l]) => <button key={id} className={`chip ${ws === id ? "on" : ""}`} onClick={() => setWs(id)}>{l}</button>)}</div>
      </div>
      <div className="card tbl-wrap"><table><thead><tr><th>{L("Name", "Emri")}</th><th>{L("Workspace", "Hapësira e punës")}</th><th>{L("Owner", "Pronari")}</th><th>{L("Shared with", "Ndarë me")}</th><th>{L("Updated", "Përditësuar")}</th></tr></thead><tbody>
        {list.map((f) => (
          <tr key={f.name}><td><div className="row" style={{ flexWrap: "nowrap" }}><span className="ico"><Icon name={f.kind} /></span><b>{f.name}</b></div></td>
            <td><Pill tone="info">{f.workspace}</Pill></td><td>{f.owner}</td>
            <td className="faint">{TEAM_IDS.every((t) => f.shared.includes(t)) && f.shared.length === TEAM_IDS.length ? L("Whole team", "I gjithë ekipi") : f.shared.map(first).join(", ")}</td><td className="faint">{f.updated}</td></tr>))}
        {!list.length && <tr><td colSpan={5} className="faint">{L("No files shared with you yet.", "Ende nuk ka skedarë të ndarë me ty.")}</td></tr>}
      </tbody></table></div>
      <p className="faint" style={{ fontSize: 12.5 }}>{L("Sample data. Files will live in VERANeural's SharePoint, one folder per workspace.", "Të dhëna shembull. Skedarët do të ruhen në SharePoint, një dosje për çdo hapësirë pune.")}</p>
    </>
  );
}

function fmtSize(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function LocalFolders() {
  const { consent, openConsent } = useStudio();
  if (desktop && !consent.folders) {
    return (
      <div className="card stack" style={{ alignItems: "flex-start", gap: 12, padding: 26 }}>
        <span className="ico"><Icon name="lock" /></span>
        <h2>{L("VERA can't read folders on this computer yet", "VERA ende nuk mund të lexojë dosje në këtë kompjuter")}</h2>
        <p className="mute" style={{ maxWidth: 620 }}>{L("You haven't turned this on. When you do, VERA can read only the folders you add, and only when you ask about them. You can turn it off any time.", "Këtë nuk e ke aktivizuar. Kur ta aktivizosh, VERA mund të lexojë vetëm dosjet që shton, dhe vetëm kur e pyet për to. Mund ta çaktivizosh në çdo kohë.")}</p>
        <button className="btn primary" onClick={openConsent}>{L("Review in Privacy & consent", "Shiko te Privatësia dhe pëlqimi")}</button>
      </div>
    );
  }
  return <LocalFolderBrowser />;
}

function LocalFolderBrowser() {
  const { go, toast } = useStudio();
  const [folders, setFolders] = useState<DesktopFolder[]>([]);
  const [open, setOpen] = useState<DesktopFolder | null>(null);
  const [path, setPath] = useState("");
  const [entries, setEntries] = useState<DesktopEntry[]>([]);
  const [preview, setPreview] = useState<{ entry: DesktopEntry; text: string; truncated: boolean } | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [question, setQuestion] = useState("");
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => { if (desktop) void desktop.folders.list().then(setFolders); }, []);
  useEffect(() => {
    if (!desktop || !open) return;
    setError("");
    setPicked([]);
    desktop.folders.browse(open.id, path).then(setEntries).catch((e: Error) => setError(e.message));
  }, [open, path]);

  if (!desktop) {
    return (
      <div className="card stack" style={{ alignItems: "flex-start", gap: 12, padding: 26 }}>
        <span className="ico"><Icon name="laptop" /></span>
        <h2>{L("Bring VERA to the folders on your computer", "Sille VERA-n te dosjet në kompjuterin tënd")}</h2>
        <p className="mute" style={{ maxWidth: 620 }}>{L("With VERA for desktop, you choose folders about your work, such as client files, proposals or job sheets, and VERA can read them when you ask. Nothing else on your computer is touched, and nothing leaves it until you ask VERA about a file.", "Me VERA për kompjuter, ti zgjedh dosjet e punës, si skedarët e klientëve ose propozimet, dhe VERA i lexon kur e pyet. Asgjë tjetër në kompjuter nuk preket, dhe asgjë nuk largohet prej tij derisa ta pyetësh VERA-n për një skedar.")}</p>
        <button className="btn primary" onClick={() => toast(L("Install VERA for desktop from the VERA Studio folder in the team's SharePoint.", "VERA për kompjuter do të jetë së shpejti në dispozicion edhe për ju. Deri atëherë, ngarko skedarët direkt në një bisedë."))}>{L("Get VERA for desktop", "Merr VERA-n për kompjuter")}</button>
      </div>
    );
  }

  async function add() {
    const f = await desktop!.folders.add();
    if (f) { setFolders(await desktop!.folders.list()); setOpen(f); setPath(""); setPreview(null); toast(Tf("VERA can now read \"{name}\" when you ask.", { name: f.name })); }
  }
  async function remove(f: DesktopFolder) {
    await desktop!.folders.remove(f.id);
    setFolders(await desktop!.folders.list());
    if (open?.id === f.id) { setOpen(null); setEntries([]); setPreview(null); }
    toast(Tf("Removed \"{name}\". VERA can no longer read it.", { name: f.name }));
  }
  async function view(e: DesktopEntry) {
    if (e.kind === "folder") { setPath(e.path); setPreview(null); return; }
    if (!e.readable) { toast(T("This file can't be previewed here. To have VERA read a PDF, Word, Excel or PowerPoint file, attach it in a conversation.")); return; }
    try { const r = await desktop!.folders.readText(open!.id, e.path); setPreview({ entry: e, text: r.text, truncated: r.truncated }); }
    catch (err) { toast(err instanceof Error ? err.message : T("Couldn't open that file.")); }
  }
  const crumbs = path ? path.split("/") : [];
  const readable = entries.filter((e) => e.kind === "file" && e.readable);
  const togglePick = (p: string) => setPicked((x) => (x.includes(p) ? x.filter((y) => y !== p) : [...x, p]));
  const MAX_FILES = 10;
  // What VERA will read: the open file, else the ticked files, else the readable files in this folder (up to 10).
  const target = preview ? [preview.entry] : picked.length ? readable.filter((e) => picked.includes(e.path)) : readable.slice(0, MAX_FILES);
  const where = crumbs.length ? crumbs[crumbs.length - 1] : open?.name ?? "";
  const about = preview ? preview.entry.name : picked.length === 1 ? (readable.find((e) => e.path === picked[0])?.name ?? where) : picked.length ? `${picked.length} selected files` : where;

  async function ask(e?: FormEvent) {
    e?.preventDefault();
    const q = question.trim();
    if (!q || !open || asking) return;
    if (!target.length) { toast(T("There's nothing VERA can read in this folder yet. Open a subfolder, or tick a text, CSV or code file.")); return; }
    setAsking(true);
    try {
      const files = await Promise.all(target.map(async (t) => {
        if (preview && t.path === preview.entry.path) return { name: t.name, text: preview.text };
        const r = await desktop!.folders.readText(open.id, t.path);
        return { name: t.name, text: r.text };
      }));
      go("workspace", { ask: q, files, fresh: true, title: `${about}: ${q}`.slice(0, 60) });
    } catch (err) {
      toast(err instanceof Error ? err.message : T("Couldn't read those files."));
    } finally {
      setAsking(false);
    }
  }

  return (
    <div className="split">
      <div className="card stack" style={{ gap: 10 }}>
        <div className="row" style={{ justifyContent: "space-between" }}><h2 style={{ fontSize: 18 }}>{T("Folders VERA can read")}</h2><button className="btn primary" onClick={() => void add()}><Icon name="plus" size={16} />{" "}{T("Add a folder")}</button></div>
        {!folders.length && <p className="faint">{T("No folders yet. Add one, such as your client folder or job sheets. You can remove it any time.")}</p>}
        {folders.map((f) => (
          <div key={f.id} className="row" style={{ flexWrap: "nowrap", gap: 10, padding: "8px 0", borderTop: "1px solid var(--line)" }}>
            <span className="ico"><Icon name="folder" /></span>
            <button className="grow" style={{ textAlign: "left", background: "none", border: 0, color: "var(--text)", minWidth: 0 }} onClick={() => { setOpen(f); setPath(""); setPreview(null); }}>
              <b>{f.name}</b><div className="faint" style={{ fontSize: 12, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.path}</div>
            </button>
            <button className="btn" onClick={() => void remove(f)}>{T("Remove")}</button>
          </div>))}
        <div className="notice calm"><span className="mute" style={{ fontSize: 13 }}>{T("VERA reads only the folders listed here, and only when you ask. Files are sent to VERA, in VERANeural's own cloud, only for the question you ask about them.")}</span></div>
      </div>
      <div className="card stack" style={{ gap: 10, minHeight: 300 }}>
        {!open ? <p className="faint">{T("Choose a folder to browse it.")}</p> : (
          <>
            <div className="crumbs"><button onClick={() => { setPath(""); setPreview(null); }}>{open.name}</button>
              {crumbs.map((c, i) => <span key={i}>/ <button onClick={() => { setPath(crumbs.slice(0, i + 1).join("/")); setPreview(null); }}>{c}</button></span>)}</div>
            {error && <p style={{ color: "var(--bad)" }}>{error}</p>}
            {preview ? (
              <>
                <div className="row" style={{ justifyContent: "space-between" }}><b>{preview.entry.name}</b><button className="btn" onClick={() => setPreview(null)}>{T("Back")}</button></div>
                <div className="preview">{preview.text}{preview.truncated && T("\n\n… (showing the first part)")}</div>
                <div className="row">
                  <button className="btn" onClick={() => void desktop!.folders.reveal(open.id, preview.entry.path)}>{T("Show in folder")}</button>
                </div>
              </>
            ) : (
              <div>
                {entries.map((e) => (
                  <div className={`filerow pickrow ${e.kind === "file" && !e.readable ? "dim" : ""}`} key={e.path}>
                    {e.kind === "file" && e.readable
                      ? <input type="checkbox" aria-label={Tf("Include {name} in the question", { name: e.name })} checked={picked.includes(e.path)} onChange={() => togglePick(e.path)} />
                      : <span className="pickspace" />}
                    <button className="filebtn" onClick={() => void view(e)}>
                      <Icon name={e.kind === "folder" ? "folder" : "doc"} size={16} /><span className="grow">{e.name}</span>
                      <span className="faint" style={{ fontSize: 12 }}>{e.kind === "folder" ? T("Folder") : e.readable ? fmtSize(e.size) : T("Not readable yet")}</span>
                    </button>
                  </div>))}
                {!entries.length && !error && <p className="faint">{T("This folder is empty.")}</p>}
              </div>
            )}
            <form className="askbar" onSubmit={(e) => void ask(e)}>
              <span className="orb" aria-hidden="true" />
              <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder={Tf("Ask VERA about {what}…", { what: about })} aria-label={Tf("Ask VERA about {what}", { what: about })} />
              <button className="btn primary" disabled={!question.trim() || asking}>{asking ? T("Reading…") : T("Ask")}</button>
            </form>
            <p className="faint" style={{ fontSize: 12, marginTop: -4 }}>
              {preview ? Tf("VERA will read {name}.", { name: preview.entry.name }) : picked.length ? (picked.length > 1 ? Tf("VERA will read the {n} files you ticked.", { n: picked.length }) : T("VERA will read the file you ticked.")) : readable.length ? (readable.length > 1 ? Tf("VERA will read {n} files in this folder.", { n: Math.min(readable.length, MAX_FILES) }) : T("VERA will read the file in this folder.")) + (readable.length > MAX_FILES ? " " + T("Only the first 10; tick files to choose.") : " " + T("Tick files to choose.")) : T("Open a file or a folder with text, CSV or code files.")}
              {" "}{T("The answer opens in Workspace and is saved in your history.")}
            </p>
          </>
        )}
      </div>
    </div>
  );
}
